import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WEATHER_LAYERS } from '../home/WeatherMapControls';

/**
 * Short forms for the segmented control, per the design. The full names in
 * WEATHER_LAYERS ("Temperature", "Precipitation") are what the dropdowns and
 * the loading toast say; five of those side by side do not fit a phone.
 */
const SHORT_LABELS = {
  temp: 'Temp',
  precip: 'Precip',
  clouds: 'Clouds',
  wind: 'Wind',
  pressure: 'Pressure',
};

/** Travel time of the pill, ms. The squash runs over half of it, twice. */
const MOVE_MS = 320;
/**
 * How far the pill compresses mid-flight.
 *
 * Narrower along the travel axis than across it, which is what makes it read
 * as a bubble being pulled rather than simply scaled down. Kept mild: past
 * roughly 0.8 the label visibly scales with it and looks like a zoom.
 */
const SQUASH_X = 0.88;
const SQUASH_Y = 0.94;

/**
 * The layer glyph, drawn from the icon exported with the design.
 *
 * It is masked rather than placed in an `<img>` because the same glyph has to
 * render white on the active pill and grey everywhere else, and an `<img>`
 * keeps whatever stroke colour the file was exported with. The mask uses only
 * the file's alpha, so `bg-current` picks up the button's own text colour and
 * the two states stay in step with one source file.
 */
const LayerIcon = ({ id, className = '' }) => {
  const url = `url(/assets/icons/weather/${id}.svg)`;
  return (
    <span
      aria-hidden="true"
      className={`block size-[16px] shrink-0 bg-current max-lg:size-[14px] max-sm:size-[12px] ${className}`}
      style={{
        maskImage: url,
        WebkitMaskImage: url,
        maskSize: '100% 100%',
        WebkitMaskSize: '100% 100%',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
      }}
    />
  );
};

/**
 * Weather layer switch — the segmented control under the map's main filters.
 *
 * Exactly one layer is drawn at a time, so this is a radio group, not a set of
 * toggles: there is no state in which none or two are chosen, and buttons that
 * can be switched off would imply otherwise.
 *
 * Picking a layer only lifts `value`; redrawing the tiles and restyling the
 * scale widget both follow from that one prop in UnifiedMapContainer, so the
 * map, the switch and the scale can never show different layers.
 *
 * The green pill is ONE element that slides between segments, rather than a
 * background on each button. Per-button backgrounds can only cross-fade — two
 * pills visible at once, neither moving — which is not what "a bubble moving
 * from one to another" means. Sliding one element also means the travel time
 * is the same whatever the distance, so neighbouring switches feel identical
 * to end-to-end ones.
 */
const WeatherLayerSwitch = ({ value, onChange, className = '' }) => {
  const { t } = useTranslation();
  const listRef = useRef(null);
  const btnRefs = useRef({});
  const [bubble, setBubble] = useState(null);
  const [squashed, setSquashed] = useState(false);
  // First paint positions the pill without animating — otherwise it flies in
  // from the left edge on mount, which reads as a transition nobody triggered.
  const settled = useRef(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = btnRefs.current[value];
      const list = listRef.current;
      if (!el || !list) return;
      setBubble({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();

    // Labels shift when the font loads or the row is resized; the pill has to
    // follow or it ends up behind the wrong segment.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && listRef.current) {
      ro.observe(listRef.current);
      Object.values(btnRefs.current).forEach((el) => el && ro.observe(el));
    }
    return () => ro?.disconnect();
  }, [value]);

  useLayoutEffect(() => {
    if (bubble) {
      const id = requestAnimationFrame(() => { settled.current = true; });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [bubble]);

  // Squash on the way out, release on the way in. Two phases rather than a
  // keyframe animation so it stays interruptible: picking a third layer
  // mid-flight restarts the timer and the pill keeps travelling, where a
  // running @keyframes would have to finish or snap.
  useEffect(() => {
    if (!settled.current) return undefined;   // no squash on first paint
    setSquashed(true);
    const id = setTimeout(() => setSquashed(false), MOVE_MS / 2);
    return () => clearTimeout(id);
  }, [value]);

  return (
    <div
      ref={listRef}
      role="radiogroup"
      aria-label={t('map.controls.weatherLayerAria', 'Map weather layer')}
      // Scales down rather than scrolling: all five layers have to stay
      // readable at once, so the type, padding and gaps shrink with the
      // viewport instead of the track clipping its tail. `overflow-x-auto`
      // stays as the last resort for the very narrowest phones.
      className={`no-scrollbar relative inline-flex h-[36px] max-w-full items-center overflow-x-auto rounded-[17px] border border-solid border-[#111827]/8 bg-white/90 p-[4px] shadow-[0px_2px_10px_0px_rgba(17,24,39,0.1)] backdrop-blur-[9px] max-lg:h-[32px] max-lg:rounded-[16px] max-lg:p-[3px] max-sm:h-[30px] max-sm:rounded-[15px] dark:border-white/8 dark:bg-[#1B1F1C]/90 dark:shadow-[0px_2px_10px_0px_#00000066] ${className}`}
    >
      {/* The travelling pill. `motion-reduce` drops the slide for users who ask
          for less motion — the colour still moves, just without the travel. */}
      {bubble && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-[4px] h-[28px] rounded-[14px] bg-bloom-green shadow-[0px_1px_2px_0px_rgba(22,163,74,0.34)] max-lg:top-[3px] max-lg:h-[26px] max-lg:rounded-[13px] max-sm:h-[24px] max-sm:rounded-[12px] motion-reduce:!transform-none motion-reduce:!transition-none"
          style={{
            left: bubble.left,
            width: bubble.width,
            // Scales about its own centre, so the pill pinches inward from
            // both ends rather than dragging one edge along.
            transform: squashed ? `scaleX(${SQUASH_X}) scaleY(${SQUASH_Y})` : 'scaleX(1) scaleY(1)',
            transition: settled.current
              ? `left ${MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1), `
                + `width ${MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1), `
                // Out quickly, back with a soft landing — an even ease both
                // ways reads as a wobble rather than one deliberate stretch.
                + `transform ${MOVE_MS / 2}ms ${squashed ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0.16, 1, 0.3, 1)'}`
              : 'none',
          }}
        />
      )}

      {WEATHER_LAYERS.map((layer) => {
        const active = layer.id === value;
        return (
          <button
            key={layer.id}
            ref={(el) => { btnRefs.current[layer.id] = el; }}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange?.(layer.id)}
            // `relative` lifts the label above the pill, which is absolute.
            className={`relative z-[1] flex h-[28px] shrink-0 items-center gap-[6px] rounded-[14px] px-[11px] font-['Inter'] text-[12px] font-semibold whitespace-nowrap transition-colors duration-200 max-lg:h-[26px] max-lg:gap-[4px] max-lg:rounded-[13px] max-lg:px-[8px] max-lg:text-[11px] max-sm:h-[24px] max-sm:gap-[3px] max-sm:rounded-[12px] max-sm:px-[6px] max-sm:text-[10px] ${
              active
                ? 'text-white'
                : 'text-[#4B5563] hover:text-[#282A29] dark:text-[#9AA09A] dark:hover:text-[#E6E8E6]'
            }`}
          >
            <LayerIcon id={layer.id} className={active ? '' : 'opacity-75'} />
            {t(`map.layers.short.${layer.id}`, SHORT_LABELS[layer.id] || layer.label)}
          </button>
        );
      })}
    </div>
  );
};

export default WeatherLayerSwitch;
